import { useEffect, useState } from 'react';
import { ArrowRight, ChevronDown } from 'lucide-react';
import { openCalendly } from '@/lib/calendly';

// Deterministic pseudo-random for particles so SSR/CSR agree and layout is stable
const PARTICLES = Array.from({ length: 14 }, (_, i) => ({
  left: `${(i * 47) % 100}%`,
  size: 3 + ((i * 5) % 6),
  delay: (i * 0.7) % 8,
  duration: 14 + (i % 6) * 3,
}));

export default function Hero() {
  const [mouse, setMouse] = useState({ x: 0, y: 0 });

  useEffect(() => {
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduce) return;
    let ticking = false;
    const onMove = (e) => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        const x = (e.clientX / window.innerWidth - 0.5) * 20;
        const y = (e.clientY / window.innerHeight - 0.5) * 20;
        setMouse({ x, y });
        ticking = false;
      });
    };
    window.addEventListener('mousemove', onMove);
    return () => window.removeEventListener('mousemove', onMove);
  }, []);

  const scrollTo = (id) => {
    const el = document.querySelector(id);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section
      id="home"
      data-testid="hero-section"
      className="relative min-h-screen w-full overflow-hidden bg-[#070B12] flex items-center pt-32 pb-24"
    >
      {/* Radial gold glows — parallax with mouse */}
      <div
        className="hero-glow breathe"
        style={{
          background: 'radial-gradient(circle, rgba(214,179,106,0.55) 0%, rgba(214,179,106,0) 70%)',
          width: '780px',
          height: '780px',
          top: '-180px',
          right: '-160px',
          transform: `translate(${mouse.x * 0.6}px, ${mouse.y * 0.6}px)`,
          transition: 'transform 1200ms cubic-bezier(0.2, 0.7, 0.2, 1)',
        }}
      />
      <div
        className="hero-glow breathe"
        style={{
          background: 'radial-gradient(circle, rgba(14,34,56,0.9) 0%, rgba(14,34,56,0) 70%)',
          width: '900px',
          height: '900px',
          bottom: '-260px',
          left: '-220px',
          animationDelay: '2s',
          transform: `translate(${-mouse.x * 0.4}px, ${-mouse.y * 0.4}px)`,
          transition: 'transform 1200ms cubic-bezier(0.2, 0.7, 0.2, 1)',
        }}
      />
      <div
        className="hero-glow"
        style={{
          background: 'radial-gradient(circle, rgba(182,138,53,0.28) 0%, rgba(182,138,53,0) 70%)',
          width: '520px',
          height: '520px',
          top: '35%',
          left: '30%',
          transform: `translate(${mouse.x * 0.3}px, ${mouse.y * 0.3}px)`,
          transition: 'transform 1200ms cubic-bezier(0.2, 0.7, 0.2, 1)',
        }}
      />

      {/* Grain */}
      <div className="absolute inset-0 hero-grain pointer-events-none" />

      {/* Floating gold particles */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        {PARTICLES.map((p, i) => (
          <span
            key={i}
            className="particle"
            style={{
              left: p.left,
              bottom: `-${p.size * 4}px`,
              width: `${p.size}px`,
              height: `${p.size}px`,
              animationDelay: `${p.delay}s`,
              animationDuration: `${p.duration}s`,
            }}
          />
        ))}
      </div>

      {/* Vertical line accents */}
      <div className="hidden md:block absolute left-10 top-1/3 h-40 w-px bg-gradient-to-b from-transparent via-[#B68A35]/40 to-transparent" />
      <div className="hidden md:block absolute right-10 top-1/2 h-40 w-px bg-gradient-to-b from-transparent via-[#B68A35]/40 to-transparent" />

      {/* Content */}
      <div className="relative z-10 max-w-6xl mx-auto px-6 md:px-10 text-center">
        {/* Editorial line */}
        <p
          className="font-serif italic text-[#D6B36A]/85 text-base sm:text-lg md:text-xl mb-7 tracking-wide fade-up"
          style={{ animationDelay: '80ms' }}
        >
          Legacy is not left to chance. It is designed.
        </p>

        {/* Hairline divider */}
        <div
          className="mx-auto mb-10 w-24 h-px bg-gradient-to-r from-transparent via-[#B68A35]/50 to-transparent fade-in"
          style={{ animationDelay: '200ms' }}
        />

        {/* Main headline */}
        <h1
          data-testid="hero-headline"
          className="font-serif text-white text-[2.7rem] sm:text-6xl md:text-7xl lg:text-[5.75rem] leading-[1.03] tracking-[-0.015em] mb-9 fade-up"
          style={{ animationDelay: '260ms' }}
        >
          Financial Strategies<br />
          Built for Your <span className="gold-text italic">Legacy</span>
        </h1>

        {/* Subheadline */}
        <p
          className="max-w-3xl mx-auto text-white/70 text-base md:text-lg leading-[1.7] font-light mb-12 fade-up"
          style={{ animationDelay: '440ms' }}
        >
          Bowers Advisory Group empowers families, educators, business owners, and faith-based communities
          with personalized financial strategies designed to protect their future, prepare for retirement, and build lasting confidence.
        </p>

        {/* CTAs */}
        <div
          className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-16 fade-up"
          style={{ animationDelay: '600ms' }}
        >
          <button
            onClick={openCalendly}
            data-testid="hero-schedule-cta"
            className="btn-gold w-full sm:w-auto"
          >
            Schedule a Consultation
            <ArrowRight className="w-4 h-4" strokeWidth={1.75} />
          </button>
          <button
            onClick={() => scrollTo('#framework')}
            data-testid="hero-framework-cta"
            className="btn-ghost w-full sm:w-auto text-white/85 border-white/25 hover:border-[#D6B36A]"
          >
            Explore the Legacy Framework
          </button>
        </div>

        {/* Trust line */}
        <div
          className="flex flex-wrap items-center justify-center gap-x-4 gap-y-2 text-white/45 text-[0.68rem] sm:text-xs tracking-[0.24em] uppercase font-light fade-up"
          style={{ animationDelay: '780ms' }}
        >
          <span>Education first guidance</span>
          <span className="text-[#B68A35]">·</span>
          <span>Insurance based strategies</span>
          <span className="text-[#B68A35]">·</span>
          <span>Legacy-centered planning</span>
        </div>
      </div>

      {/* Bottom gradient fade */}
      <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-b from-transparent to-[#070B12] pointer-events-none" />

      {/* Scroll indicator */}
      <button
        onClick={() => scrollTo('#why-us')}
        aria-label="Scroll to content"
        className="hidden md:flex absolute bottom-8 left-1/2 -translate-x-1/2 flex-col items-center gap-2 text-[#D6B36A]/60 hover:text-[#D6B36A] transition-colors z-20 group"
      >
        <span className="text-[0.65rem] tracking-[0.35em] uppercase font-light">Scroll</span>
        <div className="relative w-5 h-8 rounded-full border border-[#B68A35]/40 flex items-start justify-center pt-1.5 group-hover:border-[#D6B36A] transition-colors">
          <span className="w-1 h-1.5 rounded-full bg-[#D6B36A] scroll-dot" />
        </div>
        <ChevronDown className="w-3 h-3 opacity-60" strokeWidth={1.5} />
      </button>
    </section>
  );
}
